import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { Container, Stack, Text } from "@mantine/core";
import { useEffect } from "react";

import { isInvalidMdnError } from "../../../../api/eligibility.errors";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { IneligibleIcon, LoadingAnimation } from "../../../product-sale-flow/components/Icons";
import { useEligibility } from "../../hooks/useEligibility";
import { useProductOfferPolling } from "../../hooks/useProductOfferPolling";
import { usePushDiscoveryEngagementToTimeline } from "../../hooks/usePushDiscoveryEngagementToTimeline";
import { useQuerySelectedProductOffer } from "../../hooks/useQuerySelectedProductOffer";
import classes from "./CustomerOffers.module.css";
import { CustomerOffersLoaded } from "./CustomerOffersLoaded";
import { InvalidMdnView } from "./InvalidMdnView";

export type CustomerOffersView = "tabs" | "offers";

interface CustomerOffersProps {
    view?: CustomerOffersView;
}

export function CustomerOffers({ view = "tabs" }: CustomerOffersProps) {
    const { sessionId, logger, taskType, startedTimestamp, partner } = useSalesContext();

    useQuerySelectedProductOffer();
    useProductOfferPolling();
    usePushDiscoveryEngagementToTimeline();

    const { error, isError, isLoading } = useEligibility();
    const isInvalidMdn = isError && isInvalidMdnError(error);

    useEffect(() => {
        if (!isError) {
            return;
        }
        if (isInvalidMdnError(error)) {
            // Logged at the API layer; here we only need the distinct metric.
            void sendErrorMetric("InvalidMdnError", { eventSource: "Eligibility-FE", partner });
            return;
        }
        logger.warn({ error }, "Error checking eligibility");
        void sendErrorMetric("EligibilityError", { eventSource: "Eligibility-FE", partner });
    }, [isError, error, logger, partner]);

    if (isInvalidMdn) {
        return <InvalidMdnView sessionId={sessionId} />;
    }

    if (isError) {
        return (
            <Stack align="center" data-testid="failure" mt="sm">
                <Container>{IneligibleIcon}</Container>
                <Text className={classes.statusBodyHeader}>We had an issue checking eligibility.</Text>
                <Text className={classes.statusBodyText} data-testid="sale-view-status-body">
                    Try refreshing the page.
                </Text>
            </Stack>
        );
    }

    if (isLoading) {
        return (
            <Stack align="center" mt="sm">
                <Text size="sm">Confirming customer information...</Text>
                <Container maw="160px">
                    <LoadingAnimation />
                </Container>
            </Stack>
        );
    }

    return <CustomerOffersLoaded startedTimestamp={startedTimestamp} isChat={taskType.isChat} view={view} />;
}
